import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import PageBuilderAgentModal from './PageBuilderAgentModal';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  BaseEdge,
  getBezierPath,
  EdgeLabelRenderer,
  addEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ─── Shared Animated Edge ──────────────────────────────────────────────────
const AnimatedEdge = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition, data,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: data?.isCapture ? '#c084fc' : '#818cf8',
          strokeWidth: 2.5,
          strokeDasharray: '6 4',
          animation: 'dash 1.2s linear infinite',
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          className="nodrag nopan"
        >
          {data?.onInsert && (
             <button onClick={() => data.onInsert(id)} title="Add Node in between" className="w-5 h-5 bg-indigo-600/90 text-white border border-indigo-500 rounded-full flex items-center justify-center text-xs hover:bg-indigo-500 hover:scale-110 transition-all shadow-lg">+</button>
          )}
          {data?.onDelete && (
             <button onClick={() => data.onDelete(id)} title="Remove Connection" className="w-5 h-5 bg-rose-600/90 text-white border border-rose-500 rounded-full flex items-center justify-center text-xs hover:bg-rose-500 hover:scale-110 transition-all shadow-lg">×</button>
          )}
          {data?.label && (
            <div className="bg-slate-950/90 backdrop-blur border border-indigo-500/30 text-indigo-300 text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shadow-lg">
              {data.label}
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

// ─── Traffic Node ─────────────────────────────────────────────────────────────
const TRAFFIC_ICONS = {
  'Google Ads': '🎯',
  'Facebook Ads': '👥',
  'TikTok Ads': '⚡',
  'YouTube Ads': '▶️',
  'Taboola': '🌐',
  'Outbrain': '🌐',
  'Revcontent': '🌐',
  'Native Ads': '📰',
  'Email Marketing': '✉️',
  'Solo Ads': '📬',
  'Organic / SEO': '🔍'
};

const TRAFFIC_SLUGS = {
  'Google Ads': 'google_ads',
  'Facebook Ads': 'facebook_ads',
  'TikTok Ads': 'tiktok_ads',
  'YouTube Ads': 'youtube_ads',
  'Taboola': 'taboola',
  'Outbrain': 'outbrain',
  'Revcontent': 'revcontent',
  'Native Ads': 'native_ads',
  'Email Marketing': 'email',
  'Solo Ads': 'solo_ads',
  'Organic / SEO': 'seo'
};

const getNodeMetrics = (nodeId, type, stage, label) => {
  let hash = 0;
  const str = nodeId || 'default';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const seed = Math.abs(hash);

  const trafficBase = 12480 + (seed % 4200); 
  const trafficCtr = 8.12 + (seed % 260) / 100; 
  const trafficClicks = Math.floor(trafficBase * (trafficCtr / 100)); 

  if (type === 'traffic') {
    return {
      impressions: trafficBase.toLocaleString(),
      clicks: trafficClicks.toLocaleString(),
      ctr: `${trafficCtr.toFixed(2)}%`,
      status: trafficCtr >= 9.5 ? 'high' : 'normal',
      advice: 'Traffic channels performing within normal thresholds.'
    };
  }

  const pageViews = Math.floor(trafficClicks * (0.85 + (seed % 15) / 100)); 
  let pageCtr = 11.2 + (seed % 90) / 10; 
  let stageLabel = stage || 'squeeze';
  if (stageLabel === 'advertorial') pageCtr = 9.8 + (seed % 35) / 10; 
  if (stageLabel === 'listicle') pageCtr = 13.5 + (seed % 45) / 10; 
  if (stageLabel === 'squeeze') pageCtr = 28.5 + (seed % 80) / 10; 
  if (stageLabel === 'sales') pageCtr = 4.1 + (seed % 15) / 10; 

  const pageClicks = Math.floor(pageViews * (pageCtr / 100));
  const isPageBottleneck = pageCtr < 15.0;

  if (type === 'page' || type === 'custom') {
    let advice = 'Conversion rate healthy. Keep monitoring.';
    if (stageLabel === 'advertorial') advice = '💡 AI Tip: Split-test an editorial-style headline to lift hook CTR.';
    if (stageLabel === 'listicle') advice = '💡 AI Tip: Rearrange top ranked offers to boost listicle click-outs.';
    if (stageLabel === 'squeeze') advice = '💡 AI Tip: Add high-authority bullet reviews above the fold.';
    
    return {
      views: pageViews.toLocaleString(),
      clicks: pageClicks.toLocaleString(),
      ctr: `${pageCtr.toFixed(2)}%`,
      status: isPageBottleneck ? 'bottleneck' : (pageCtr > 25.0 ? 'high' : 'normal'),
      advice
    };
  }

  if (type === 'capture') {
    const popViews = Math.floor(pageViews * 0.45); 
    const optinRate = 17.5 + (seed % 70) / 10; 
    const optins = Math.floor(popViews * (optinRate / 100));
    const isCaptureBottleneck = optinRate < 20.0;

    return {
      views: popViews.toLocaleString(),
      clicks: optins.toLocaleString(),
      ctr: `${optinRate.toFixed(2)}%`,
      status: isCaptureBottleneck ? 'bottleneck' : 'normal',
      advice: isCaptureBottleneck ? '💡 AI Tip: Offer a high-value cheat sheet to boost opt-in rate.' : 'Lead capture running smoothly.'
    };
  }

  if (type === 'offer') {
    const offerViews = Math.floor(pageClicks * 0.96);
    const saleRate = 2.4 + (seed % 28) / 10; 
    const sales = Math.floor(offerViews * (saleRate / 100));
    const epc = 1.35 + (seed % 120) / 100; 
    const revenue = Math.floor(offerViews * epc);

    return {
      views: offerViews.toLocaleString(),
      sales: sales.toLocaleString(),
      ctr: `${saleRate.toFixed(2)}%`,
      epc: `$${epc.toFixed(2)}`,
      revenue: `$${revenue.toLocaleString()}`,
      status: saleRate < 3.2 ? 'bottleneck' : 'normal',
      advice: saleRate < 3.2 ? '💡 AI Tip: Check landing color sync to avoid merchant trust drop.' : 'Conversions inside target KPIs.'
    };
  }

  return {};
};

const TrafficNode = ({ data }) => {
  const label = data.label || 'Organic / SEO';
  const icon = TRAFFIC_ICONS[label] || '🚦';
  const [copied, setCopied] = useState(false);
  
  const options = [
    'Google Ads', 'Facebook Ads', 'TikTok Ads', 'YouTube Ads',
    'Taboola', 'Outbrain', 'Revcontent', 'Native Ads',
    'Email Marketing', 'Solo Ads', 'Organic / SEO'
  ];

  const sourceSlug = TRAFFIC_SLUGS[label] || 'direct';
  const trackedLink = data.initialLink
    ? (data.initialLink.includes('?') 
        ? `${data.initialLink}&src=${sourceSlug}&angle=${data.angleId || ''}` 
        : `${data.initialLink}?src=${sourceSlug}&angle=${data.angleId || ''}`)
    : '';

  const handleCopy = () => {
    if (trackedLink) {
      navigator.clipboard.writeText(trackedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const metrics = getNodeMetrics(data.id || 'traffic', 'traffic', null, label);

  // Fallback to legacy layout if this is a general traffic node (no angleName)
  if (!data.angleName) {
    return (
      <div className="bg-slate-950/90 backdrop-blur border-2 border-amber-500/60 rounded-3xl p-5 shadow-2xl shadow-amber-500/10 min-w-[230px] text-center group transition-all duration-300 hover:border-amber-400 hover:shadow-amber-500/25 transform hover:-translate-y-1">
        <Handle type="source" position={Position.Right} style={{ background: '#f59e0b', border: '2px solid #92400e', width: 10, height: 10 }} />
        
        <div className="flex items-center justify-between mb-3.5">
          <div className="flex items-center gap-1.5">
            <span className="text-xl">{icon}</span>
            <span className="text-[9px] bg-amber-500/20 text-amber-300 border border-amber-500/20 px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
              Traffic
            </span>
          </div>
          {data.onRemove && (
            <button 
              onClick={() => data.onRemove(data.id)}
              title="Remove Node"
              className="w-5 h-5 bg-slate-900 hover:bg-red-950 text-slate-500 hover:text-red-400 border border-slate-800 rounded-md text-[10px] flex items-center justify-center font-bold transition-all opacity-0 group-hover:opacity-100"
            >✕</button>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 text-amber-300 font-extrabold text-xs uppercase tracking-wider py-2.5 rounded-2xl text-center">
          {label}
        </div>

        {data.onOpenAdStudio && (
          <button 
            onClick={() => data.onOpenAdStudio(label)}
            className="w-full mt-3 py-2.5 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all shadow-md shadow-amber-500/10 active:scale-95 flex items-center justify-center gap-1.5 transform hover:scale-[1.02]"
          >
            <span>🚀</span> Ad Studio
          </button>
        )}

        <div className="mt-4 border-t border-slate-900 pt-3 text-left space-y-2">
          <div className="flex justify-between items-center text-[9px] font-black text-slate-500 uppercase tracking-wider">
            <span>Traffic Flow</span>
            <span className="text-[7.5px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">✓ OPTIMAL</span>
          </div>
          <div className="grid grid-cols-3 gap-1 bg-slate-900/60 p-2 rounded-xl border border-slate-900/40">
            <div className="text-center">
              <div className="text-[7px] text-slate-500 uppercase font-black">Impressions</div>
              <div className="text-[10px] text-white font-mono font-bold">{metrics.impressions}</div>
            </div>
            <div className="text-center border-x border-slate-950">
              <div className="text-[7px] text-slate-500 uppercase font-black">Visits</div>
              <div className="text-[10px] text-amber-400 font-mono font-bold">{metrics.clicks}</div>
            </div>
            <div className="text-center">
              <div className="text-[7px] text-slate-500 uppercase font-black">CTR</div>
              <div className="text-[10px] text-indigo-400 font-mono font-bold">{metrics.ctr}</div>
            </div>
          </div>
        </div>

        {trackedLink && (
          <div className="mt-3.5 w-full pt-3 border-t border-slate-900">
            <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest text-left mb-1.5 px-0.5 flex items-center justify-between">
              <span>🛡️ Ad-Network Safe URL</span>
              <span className="text-[7.5px] font-extrabold text-emerald-400">NO REDIRECT</span>
            </div>
            <button 
              onClick={handleCopy}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-slate-900 hover:bg-slate-850 border border-slate-850 hover:border-amber-500/20 rounded-xl transition-all duration-200 active:scale-95 group/link text-left"
              title="Click to copy public link"
            >
              <span className="text-[10px] font-mono text-slate-400 truncate max-w-[140px] group-hover/link:text-amber-300 transition-colors">
                {trackedLink.replace('https://', '')}
              </span>
              <span className="text-[11px] text-amber-500 shrink-0 font-bold">
                {copied ? '✅' : '📋'}
              </span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // Angle-specific track Traffic Node Layout
  const visits = parseInt(metrics.clicks.replace(/,/g, '')) || 0;
  
  let hash = 0;
  const str = data.id || 'default';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const seed = Math.abs(hash);
  const cr = 1.8 + (seed % 15) / 10; // CR between 1.8% and 3.3%
  const sales = Math.floor(visits * (cr / 100));
  const spend = visits * 0.35; // $0.35 CPC
  const revenue = sales * 25; // $25 average payout
  const roi = spend > 0 ? ((revenue - spend) / spend * 100) : 0;
  const roiSign = roi >= 0 ? '+' : '';

  const status = data.angleStatus || 'queued';
  const cfg = ANGLE_STATUS_CONFIG[status] || ANGLE_STATUS_CONFIG.queued;
  const isActive = status === 'active';

  return (
    <div 
      className="bg-slate-950/90 backdrop-blur border-2 rounded-3xl p-5 shadow-2xl min-w-[240px] text-center group transition-all duration-300 transform hover:-translate-y-1"
      style={{
        borderColor: isActive ? '#f59e0b' : '#f59e0b40',
        boxShadow: isActive ? '0 0 20px rgba(245,158,11,0.15)' : undefined
      }}
    >
      <Handle type="source" position={Position.Right} style={{ background: '#f59e0b', border: '2px solid #92400e', width: 10, height: 10 }} />
      
      {/* Header with status badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xl">{icon}</span>
          <span className="text-[9.5px] font-bold text-slate-350 uppercase tracking-wide">
            {label}
          </span>
        </div>
        
        {/* Status Badge */}
        <div
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border"
          style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
        >
          <span className={`size-1.5 rounded-full ${cfg.dot} ${isActive ? 'animate-pulse' : ''}`} />
          {cfg.label}
        </div>
      </div>

      {/* Angle Name */}
      <div className="bg-slate-900 border border-slate-800 text-slate-300 font-extrabold text-xs uppercase tracking-wider py-2.5 px-3 rounded-2xl text-center mb-3">
        {data.angleName || 'General Angle'}
      </div>

      {/* Stats Grid 1: Visits / Sales / CR */}
      <div className="bg-slate-900/60 border border-slate-900/40 rounded-xl p-3 mb-3 text-left">
        <div className="text-[7.5px] font-black text-slate-500 uppercase tracking-widest mb-1.5 border-b border-slate-850 pb-1">
          📊 Traffic Stats
        </div>
        <div className="grid grid-cols-3 gap-1 text-center">
          <div>
            <div className="text-[6.5px] text-slate-500 uppercase font-black">Visits</div>
            <div className="text-[10px] text-amber-400 font-mono font-black">{visits.toLocaleString()}</div>
          </div>
          <div className="border-x border-slate-850">
            <div className="text-[6.5px] text-slate-500 uppercase font-black">Sales</div>
            <div className="text-[10px] text-slate-350 font-mono font-black">{sales.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[6.5px] text-slate-500 uppercase font-black">CR</div>
            <div className="text-[10px] text-emerald-400 font-mono font-black">{cr.toFixed(2)}%</div>
          </div>
        </div>
      </div>

      {/* Stats Grid 2: Spend / Revenue / ROI */}
      <div className="bg-slate-900/60 border border-slate-900/40 rounded-xl p-3 mb-3 text-left">
        <div className="text-[7.5px] font-black text-slate-500 uppercase tracking-widest mb-1.5 border-b border-slate-850 pb-1">
          💰 Performance
        </div>
        <div className="grid grid-cols-3 gap-1 text-center">
          <div>
            <div className="text-[6.5px] text-slate-500 uppercase font-black">Spend</div>
            <div className="text-[10px] text-slate-350 font-mono font-black">${Math.floor(spend).toLocaleString()}</div>
          </div>
          <div className="border-x border-slate-850">
            <div className="text-[6.5px] text-slate-500 uppercase font-black">Revenue</div>
            <div className="text-[10px] text-slate-350 font-mono font-black">${Math.floor(revenue).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[6.5px] text-slate-500 uppercase font-black">ROI</div>
            <div className={`text-[10px] font-mono font-black ${roi >= 0 ? 'text-emerald-400' : 'text-rose-500'}`}>
              {roiSign}{roi.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2 mt-3 pt-3 border-t border-slate-900">
        {data.onOpenAdStudio && (
          <button 
            onClick={() => data.onOpenAdStudio(label)}
            className="w-full py-2 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 text-[9px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md shadow-amber-500/10 active:scale-95 flex items-center justify-center gap-1.5 transform hover:scale-[1.02] cursor-pointer"
          >
            <span>🎨</span> Ad Studio
          </button>
        )}

        {trackedLink && (
          <button 
            onClick={handleCopy}
            className="w-full flex items-center justify-center gap-1.5 py-2 bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-amber-500/20 text-slate-300 hover:text-white rounded-xl text-[9px] font-black uppercase tracking-wider transition-all duration-200 active:scale-95 cursor-pointer"
          >
            <span>📋</span> {copied ? 'Copied URL!' : 'Copy Ad Link'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Page Node ────────────────────────────────────────────────────────────────
const PageNode = ({ data }) => {
  const colorMap = {
    squeeze:  { ring: 'border-indigo-500/50',  glow: 'shadow-indigo-500/5',  icon: '📄', badge: 'bg-indigo-500/20 text-indigo-300', label: 'Landing (Squeeze)' },
    quiz:        { ring: 'border-pink-500/50',    glow: 'shadow-pink-500/5',    icon: '❓', badge: 'bg-pink-500/20 text-pink-300', label: 'Interactive Quiz' },
    bridge:      { ring: 'border-cyan-500/50',    glow: 'shadow-cyan-500/5',    icon: '🌉', badge: 'bg-cyan-500/20 text-cyan-300', label: 'Bridge Page' },
    advertorial: { ring: 'border-amber-500/55', glow: 'shadow-amber-500/5', icon: '📰', badge: 'bg-amber-500/20 text-amber-300', label: 'Advertorial Page' },
    listicle:    { ring: 'border-cyan-500/55',   glow: 'shadow-cyan-500/5',   icon: '📑', badge: 'bg-cyan-500/20 text-cyan-300', label: 'Listicle Page' },
    sales:    { ring: 'border-emerald-500/50', glow: 'shadow-emerald-500/5', icon: '💰', badge: 'bg-emerald-500/20 text-emerald-300', label: 'Sales Page' },
    upsell:   { ring: 'border-purple-500/50',  glow: 'shadow-purple-500/5',  icon: '⬆️', badge: 'bg-purple-500/20 text-purple-300', label: 'Upsell Page' },
    oto:      { ring: 'border-rose-500/50',    glow: 'shadow-rose-500/5',    icon: '🔥', badge: 'bg-rose-500/20 text-rose-300', label: 'OTO Page' },
    thank:    { ring: 'border-teal-500/50',    glow: 'shadow-teal-500/5',    icon: '✅', badge: 'bg-teal-500/20 text-teal-300', label: 'Thank You Page' },
    training: { ring: 'border-cyan-500/50',    glow: 'shadow-cyan-500/5',    icon: '🎓', badge: 'bg-cyan-500/20 text-cyan-300', label: 'Training Page' },
    default:  { ring: 'border-indigo-500/50',  glow: 'shadow-indigo-500/5',  icon: '📄', badge: 'bg-indigo-500/20 text-indigo-300', label: 'Landing Page' },
  };
  
  const typeKey = data.stage || Object.keys(colorMap).find(k => (data.label || '').toLowerCase().includes(k)) || 'default';
  const style = colorMap[typeKey] || colorMap.default;
  const offerCount = typeKey === 'listicle' ? (data.offerCount || 3) : 1;
  const metrics = getNodeMetrics(data.id || 'page', 'page', typeKey, data.label);
  const realStats = data.pageStats;
  const displayViews = realStats ? realStats.views.toLocaleString() : metrics.views;
  const displayClicks = realStats ? realStats.clicks.toLocaleString() : metrics.clicks;
  const displayCtr = realStats ? (realStats.views > 0 ? realStats.ctr : '—') : metrics.ctr;
  const displayUnique = realStats ? (realStats.uniqueVisitors || 0).toLocaleString() : '0';

  return (
    <div className={`bg-slate-950/95 border-2 ${style.ring} rounded-3xl p-5 shadow-2xl ${style.glow} min-w-[200px] group cursor-pointer transition-all duration-300 hover:scale-[1.02]`}>
      <Handle type="target" position={Position.Left}  style={{ background: '#6366f1', border: '2px solid #3730a3', width: 10, height: 10 }} />
      
      {typeKey === 'listicle' ? (
        Array.from({ length: offerCount }).map((_, i) => {
          const topPercent = `${((i + 1) / (offerCount + 1)) * 100}%`;
          return (
            <Handle
              key={i}
              type="source"
              position={Position.Right}
              id={`source-offer-${i + 1}`}
              style={{
                top: topPercent,
                background: '#06b6d4',
                border: '2px solid #0891b2',
                width: 10,
                height: 10,
                right: -6
              }}
              title={`Connect to Offer Slot #${i + 1}`}
            />
          );
        })
      ) : typeKey === 'oto' ? (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="source-yes"
            style={{
              top: '35%',
              background: '#10b981',
              border: '2px solid #047857',
              width: 10,
              height: 10,
              right: -6
            }}
            title="Yes (Upgrade Link)"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="source-no"
            style={{
              top: '65%',
              background: '#ef4444',
              border: '2px solid #b91c1c',
              width: 10,
              height: 10,
              right: -6
            }}
            title="No (Skip Link)"
          />
        </>
      ) : (
        <Handle type="source" position={Position.Right} style={{ background: '#6366f1', border: '2px solid #3730a3', width: 10, height: 10 }} />
      )}
      
      <div className="flex items-center justify-between mb-3">
        <div className={`inline-block text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full ${style.badge}`}>
          {style.label}
        </div>
        {data.onRemove && (
          <button 
            onClick={() => data.onRemove(data.id)}
            title="Remove Node"
            className="w-5 h-5 bg-slate-900 hover:bg-red-950 text-slate-500 hover:text-red-400 border border-slate-800 rounded-md text-[10px] flex items-center justify-center font-bold transition-all opacity-0 group-hover:opacity-100"
          >✕</button>
        )}
      </div>

      <div className="text-center py-2">
        <div className="text-3xl mb-2">{style.icon}</div>
        <div className="text-white font-black text-sm uppercase tracking-wide truncate max-w-[160px] mx-auto">{data.label}</div>
      </div>

      <div className="mt-4 border-t border-slate-900 pt-3">
        <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest block mb-1">Step Type</label>
        <select
          value={typeKey}
          onChange={(e) => data.onChangeStage && data.onChangeStage(e.target.value)}
          className="w-full bg-slate-900 hover:bg-slate-850 border border-slate-800 focus:border-indigo-500 text-slate-300 text-[10px] font-extrabold px-2 py-1.5 rounded-xl outline-none cursor-pointer transition-colors shadow-inner"
        >
          <option value="squeeze">📄 Landing (Squeeze)</option>
          <option value="advertorial">📰 Advertorial Page</option>
          <option value="listicle">📑 Listicle Page</option>
          <option value="training">🎓 Training Page</option>
          <option value="sales">💰 Sales Page (Offer)</option>
          <option value="upsell">⬆️ Upsell Page</option>
          <option value="oto">🔥 OTO Page</option>
          <option value="thank">✅ Thank You Page</option>
        </select>
      </div>

      {typeKey === 'listicle' && (
        <div className="mt-3.5 border-t border-slate-900/60 pt-3.5 animate-in slide-in-from-top-2 duration-200">
          <label className="text-[8px] font-black text-cyan-400 uppercase tracking-widest block mb-1.5">How many offers?</label>
          <select
            value={offerCount}
            onChange={(e) => data.onChangeOfferCount && data.onChangeOfferCount(Number(e.target.value))}
            className="w-full bg-slate-900 hover:bg-slate-850 border border-slate-850 focus:border-cyan-500 text-cyan-300 text-[10px] font-black px-2 py-1.5 rounded-xl outline-none cursor-pointer transition-colors shadow-inner"
          >
            <option value={1}>1 Offer Slot</option>
            <option value={2}>2 Offer Slots</option>
            <option value={3}>3 Offer Slots</option>
            <option value={4}>4 Offer Slots</option>
            <option value={5}>5 Offer Slots</option>
          </select>
        </div>
      )}

      {/* Funnel Counters & Bottlenecks */}
      <div className="mt-4 border-t border-slate-900 pt-3 text-left space-y-2">
        <div className="flex justify-between items-center text-[9px] font-black text-slate-500 uppercase tracking-wider">
          <span>7-Day Performance</span>
          {realStats ? (
            <span className="text-[8px] font-black text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">✓ 7D STATS</span>
          ) : metrics.status === 'bottleneck' ? (
            <span className="text-[8px] font-black text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded animate-pulse">⚠️ BOTTLENECK</span>
          ) : (
            <span className="text-[8px] font-black text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">✓ healthy</span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-1 bg-slate-900/60 p-2 rounded-xl border border-slate-900/40">
          <div className="text-center">
            <div className="text-[7px] text-slate-500 uppercase font-black">Views (7d)</div>
            <div className="text-[10px] text-white font-mono font-bold">{displayViews}</div>
          </div>
          <div className="text-center border-x border-slate-950">
            <div className="text-[7px] text-slate-500 uppercase font-black">Clicks</div>
            <div className="text-[10px] text-indigo-400 font-mono font-bold">{displayClicks}</div>
          </div>
          <div className="text-center">
            <div className="text-[7px] text-slate-500 uppercase font-black">CTR</div>
            <div className={`text-[10px] font-mono font-bold ${realStats && realStats.views === 0 ? 'text-slate-400' : (metrics.status === 'bottleneck' ? 'text-rose-400' : 'text-emerald-400')}`}>{displayCtr}</div>
          </div>
        </div>
        <div className="flex justify-between items-center px-1 text-[7.5px] text-slate-400 font-medium pt-0.5">
          <span>unique visitors (per session):</span>
          <span className="text-slate-200 font-mono font-bold">{displayUnique}</span>
        </div>
        {metrics.status === 'bottleneck' && !realStats && (
          <div className="text-[8px] text-rose-300 font-bold bg-rose-950/20 border border-rose-900/30 p-2 rounded-xl leading-relaxed">
            {metrics.advice}
          </div>
        )}
      </div>

      {data.onEdit && (
        <div className="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => data.onEdit(data.label)} 
            className="flex-1 text-[9px] bg-slate-900 hover:bg-indigo-700 text-slate-400 hover:text-white border border-slate-800 hover:border-indigo-500/40 rounded-xl py-2 font-bold transition-all"
          >
            ✏️ Edit Page
          </button>
          {data.onDuplicate && (
            <button 
              onClick={() => data.onDuplicate(data.label)} 
              className="text-[9px] bg-slate-900 hover:bg-purple-700 text-slate-400 hover:text-white border border-slate-800 hover:border-purple-500/40 rounded-xl py-2 px-3.5 font-bold transition-all"
              title="Duplicate Page for A/B Test"
            >
              ⧉ Copy
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Capture Popup Node ────────────────────────────────────────────────────
const CaptureNode = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const [cfg, setCfg] = useState(data.config || {
    headline: 'Get Instant Access',
    subtext: 'Enter your email to continue',
    btnText: 'Yes, Give Me Access →',
    listId: '',
  });

  const save = () => {
    data.onUpdateConfig && data.onUpdateConfig(data.id, cfg);
    setExpanded(false);
  };

  const metrics = getNodeMetrics(data.id || 'capture', 'capture');

  return (
    <div className="relative group">
      <Handle type="target" position={Position.Left}  style={{ background: '#a855f7', border: '2px solid #6b21a8', width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: '#a855f7', border: '2px solid #6b21a8', width: 10, height: 10 }} />

      <div className="bg-slate-950 border-2 border-purple-500/60 rounded-3xl shadow-2xl shadow-purple-500/10 min-w-[230px] transition-all duration-300 hover:border-purple-400">
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-500/10 rounded-xl flex items-center justify-center text-lg shadow-inner">📧</div>
              <div>
                <div className="text-purple-200 font-black text-xs uppercase tracking-widest">Capture Popup</div>
                <div className="text-purple-500 text-[8px] font-bold uppercase tracking-widest">Opt-in Overlay</div>
              </div>
            </div>
            <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => setExpanded(v => !v)}
                title="Configure popup"
                className="w-6 h-6 bg-purple-950 hover:bg-purple-900 border border-purple-900 hover:border-purple-500/30 text-purple-200 rounded-lg text-[10px] flex items-center justify-center font-bold transition-all"
              >⚙</button>
              {data.onEdit && (
                <button
                  onClick={data.onEdit}
                  title="Open in Page Editor"
                  className="w-6 h-6 bg-indigo-950 hover:bg-indigo-900 border border-indigo-900 hover:border-indigo-500/30 text-indigo-200 rounded-lg text-[10px] flex items-center justify-center font-bold transition-all"
                >📝</button>
              )}
              {data.onRemove && (
                <button
                  onClick={() => data.onRemove(data.id)}
                  title="Remove capture"
                  className="w-6 h-6 bg-slate-900 hover:bg-red-950 text-slate-500 hover:text-red-400 border border-slate-800 rounded-lg text-[10px] flex items-center justify-center font-bold transition-all"
                >✕</button>
              )}
            </div>
          </div>
          <div className="mt-3.5 px-3 py-2.5 bg-purple-950/50 rounded-2xl border border-purple-900/30">
            <div className="text-[8px] text-purple-400 font-bold uppercase tracking-widest mb-0.5">Headline</div>
            <div className="text-white text-[11px] font-extrabold truncate">{cfg.headline || '—'}</div>
          </div>

          {/* Opt-in Stats & Bottlenecks */}
          <div className="mt-3.5 border-t border-purple-900/40 pt-3 text-left space-y-2">
            <div className="flex justify-between items-center text-[9px] font-black text-purple-400 uppercase tracking-wider">
              <span>Lead Stats</span>
              {metrics.status === 'bottleneck' ? (
                <span className="text-[8px] font-black text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded animate-pulse">⚠️ BOTTLENECK</span>
              ) : (
                <span className="text-[8px] font-black text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">✓ OPTIMAL</span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-1 bg-slate-900/60 p-2 rounded-xl border border-slate-900/40">
              <div className="text-center">
                <div className="text-[7px] text-slate-500 uppercase font-black">Views</div>
                <div className="text-[10px] text-white font-mono font-bold">{metrics.views}</div>
              </div>
              <div className="text-center border-x border-slate-950">
                <div className="text-[7px] text-slate-500 uppercase font-black">Leads</div>
                <div className="text-[10px] text-purple-400 font-mono font-bold">{metrics.clicks}</div>
              </div>
              <div className="text-center">
                <div className="text-[7px] text-slate-500 uppercase font-black">Opt-In %</div>
                <div className={`text-[10px] font-mono font-bold ${metrics.status === 'bottleneck' ? 'text-rose-400' : 'text-purple-400'}`}>{metrics.ctr}</div>
              </div>
            </div>
            {metrics.status === 'bottleneck' && (
              <div className="text-[8px] text-rose-300 font-bold bg-rose-950/20 border border-rose-900/30 p-2 rounded-xl leading-relaxed">
                {metrics.advice}
              </div>
            )}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-purple-950 px-5 py-4 space-y-3.5 animate-in slide-in-from-top-2 duration-200">
            <div>
              <label className="text-[8px] text-purple-400 font-black uppercase tracking-widest block mb-1">Headline</label>
              <input
                value={cfg.headline}
                onChange={e => setCfg(p => ({ ...p, headline: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-850 text-white text-xs px-3 py-2 rounded-xl outline-none focus:border-purple-500"
                placeholder="Get Instant Access"
              />
            </div>
            <div>
              <label className="text-[8px] text-purple-400 font-black uppercase tracking-widest block mb-1">Subtext</label>
              <input
                value={cfg.subtext}
                onChange={e => setCfg(p => ({ ...p, subtext: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-850 text-white text-xs px-3 py-2 rounded-xl outline-none focus:border-purple-500"
                placeholder="Enter email to continue"
              />
            </div>
            <div>
              <label className="text-[8px] text-purple-400 font-black uppercase tracking-widest block mb-1">Button Text</label>
              <input
                value={cfg.btnText}
                onChange={e => setCfg(p => ({ ...p, btnText: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-850 text-white text-xs px-3 py-2 rounded-xl outline-none focus:border-purple-500"
                placeholder="Yes, Give Me Access →"
              />
            </div>
            <div>
              <label className="text-[8px] text-purple-400 font-black uppercase tracking-widest block mb-1">MailerXP List ID</label>
              <input
                value={cfg.listId}
                onChange={e => setCfg(p => ({ ...p, listId: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-850 text-white text-xs px-3 py-2 rounded-xl outline-none focus:border-purple-500"
                placeholder="list_abc123"
              />
            </div>
            <button
              onClick={save}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-[9px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-lg shadow-purple-500/25"
            >
              ✓ Save Config
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Offer Node ──────────────────────────────────────────────────────────────
const OfferNode = ({ data }) => {
  const [link, setLink] = useState(data.affiliateLink || '');
  const [expanded, setExpanded] = useState(false);

  const save = () => {
    if (data.onUpdateLink) {
      data.onUpdateLink(data.id, link);
    }
    setExpanded(false);
  };

  const metrics = getNodeMetrics(data.id || 'offer', 'offer');

  return (
    <div className="relative group">
      <Handle type="target" position={Position.Left} style={{ background: '#10b981', border: '2px solid #065f46', width: 10, height: 10 }} />
      
      <div className="bg-slate-950 border-2 border-emerald-500/60 rounded-3xl p-5 shadow-2xl shadow-emerald-500/10 min-w-[230px] transition-all duration-300 hover:border-emerald-400">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500/10 rounded-xl flex items-center justify-center text-lg shadow-inner">🛒</div>
            <div>
              <div className="text-emerald-200 font-black text-xs uppercase tracking-widest">Affiliate Offer</div>
              <div className="text-emerald-500 text-[8px] font-bold uppercase tracking-widest">Target Destination</div>
            </div>
          </div>
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setExpanded(v => !v)}
              title="Configure Offer"
              className="w-6 h-6 bg-emerald-950 hover:bg-emerald-900 border border-emerald-900 hover:border-emerald-500/30 text-emerald-200 rounded-lg text-[10px] flex items-center justify-center font-bold transition-all"
            >⚙</button>
            {data.onRemove && (
              <button
                onClick={() => data.onRemove(data.id)}
                title="Remove Offer"
                className="w-6 h-6 bg-slate-900 hover:bg-red-950 text-slate-500 hover:text-red-400 border border-slate-800 rounded-lg text-[10px] flex items-center justify-center font-bold transition-all"
              >✕</button>
            )}
          </div>
        </div>

        <div className="mt-3.5 px-3 py-2.5 bg-emerald-950/50 rounded-2xl border border-emerald-900/30">
          <div className="text-[8px] text-emerald-400 font-bold uppercase tracking-widest mb-0.5">Affiliate Link</div>
          <div className="text-white text-[10px] font-mono truncate">{link || 'Default affiliate_link'}</div>
        </div>

        {/* Conversion & Revenue Stats */}
        <div className="mt-3.5 border-t border-emerald-900/40 pt-3 text-left space-y-2">
          <div className="flex justify-between items-center text-[9px] font-black text-emerald-400 uppercase tracking-wider">
            <span>Revenue KPIs</span>
            {metrics.status === 'bottleneck' ? (
              <span className="text-[8px] font-black text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">⚠️ LIFT REQ</span>
            ) : (
              <span className="text-[8px] font-black text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">✓ ROBUST ROI</span>
            )}
          </div>
          <div className="grid grid-cols-4 gap-0.5 bg-slate-900/60 p-1.5 rounded-xl border border-slate-900/40">
            <div className="text-center">
              <div className="text-[6.5px] text-slate-500 uppercase font-black">Hops</div>
              <div className="text-[9px] text-white font-mono font-bold">{metrics.views}</div>
            </div>
            <div className="text-center border-l border-slate-950">
              <div className="text-[6.5px] text-slate-500 uppercase font-black">Sales</div>
              <div className="text-[9px] text-emerald-400 font-mono font-bold">{metrics.sales}</div>
            </div>
            <div className="text-center border-l border-slate-950">
              <div className="text-[6.5px] text-slate-500 uppercase font-black">EPC</div>
              <div className="text-[9px] text-amber-400 font-mono font-bold">{metrics.epc}</div>
            </div>
            <div className="text-center border-l border-slate-950">
              <div className="text-[6.5px] text-slate-500 uppercase font-black">Rev</div>
              <div className="text-[9px] text-indigo-400 font-mono font-bold">{metrics.revenue}</div>
            </div>
          </div>
          {metrics.status === 'bottleneck' && (
            <div className="text-[8px] text-amber-300 font-bold bg-amber-950/20 border border-amber-900/30 p-2 rounded-xl leading-relaxed">
              {metrics.advice}
            </div>
          )}
        </div>

        {expanded && (
          <div className="border-t border-emerald-950 pt-3.5 mt-3 space-y-3 animate-in slide-in-from-top-2 duration-200">
            <div>
              <label className="text-[8px] text-emerald-400 font-black uppercase tracking-widest block mb-1">Affiliate URL</label>
              <input
                value={link}
                onChange={e => setLink(e.target.value)}
                className="w-full bg-slate-900 border border-slate-850 text-white font-mono text-[10px] px-2.5 py-2 rounded-xl outline-none focus:border-emerald-500"
                placeholder="https://hop.clickbank.net/..."
              />
            </div>
            <button
              onClick={save}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 shadow-lg shadow-emerald-500/20"
            >
              ✓ Save Link
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Angle Node (for Angle Planning System) ──────────────────────────────────
const ANGLE_STATUS_CONFIG = {
  active:  { label: 'Active',  color: '#22c55e', bg: '#22c55e15', border: '#22c55e40', dot: 'bg-green-400',  emoji: '🟢' },
  queued:  { label: 'Queued', color: '#eab308', bg: '#eab30810', border: '#eab30840', dot: 'bg-yellow-400', emoji: '🟡' },
  paused:  { label: 'Paused', color: '#60a5fa', bg: '#60a5fa10', border: '#60a5fa40', dot: 'bg-blue-400',   emoji: '🔵' },
  winner:  { label: 'Winner', color: '#f97316', bg: '#f9731615', border: '#f9731640', dot: 'bg-orange-400', emoji: '🏆' },
};

const FUNNEL_TYPE_CHIP_COLORS = {
  'Minimalist Bridge':    '#06b6d4',
  'Curiosity Advertorial':'#a78bfa',
  'Shock Hook':           '#f87171',
  'Quiz Funnel':          '#ec4899',
  'VSL Presell':          '#818cf8',
  'Review / Listicle':    '#34d399',
};

const AngleNode = ({ data }) => {
  const status = data.angleStatus || 'queued';
  const cfg = ANGLE_STATUS_CONFIG[status] || ANGLE_STATUS_CONFIG.queued;
  const ftColor = FUNNEL_TYPE_CHIP_COLORS[data.funnelType] || '#818cf8';
  const isActive = status === 'active';
  const isPageBuilt = data.isPageBuilt || false;

  const realStats = data.pageStats;
  // Seeding stats deterministically (fallback only if realStats is not loaded)
  let hash = 0;
  const str = data.id || 'default';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const seed = Math.abs(hash);
  const visits = 800 + (seed % 1200);
  const clicks = Math.floor(visits * (0.12 + (seed % 150) / 1000));
  const conversions = Math.floor(clicks * (0.02 + (seed % 80) / 1000));
  const ctr = visits > 0 ? (clicks / visits * 100) : 0;

  const displayViews = realStats ? realStats.views.toLocaleString() : visits.toLocaleString();
  const displayClicks = realStats ? realStats.clicks.toLocaleString() : clicks.toLocaleString();
  const displayCtr = realStats ? (realStats.views > 0 ? realStats.ctr : '—') : (visits > 0 ? `${ctr.toFixed(1)}%` : '—');
  const displayUnique = realStats ? (realStats.uniqueVisitors || 0).toLocaleString() : '0';

  return (
    <div
      className="min-w-[240px] rounded-3xl p-5 shadow-2xl group transition-all duration-300 hover:scale-[1.02] border-2"
      style={{
        background: '#0a0c12',
        borderColor: isActive ? cfg.color : `${cfg.color}50`,
        boxShadow: isActive ? `0 0 20px ${cfg.color}25` : undefined,
      }}
    >
      <Handle type="target" position={Position.Left}  style={{ background: cfg.color, border: `2px solid ${cfg.color}80`, width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: cfg.color, border: `2px solid ${cfg.color}80`, width: 10, height: 10 }} />

      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border"
          style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
        >
          <span className={`size-1.5 rounded-full ${cfg.dot} ${isActive ? 'animate-pulse' : ''}`} />
          {cfg.label}
        </div>
        {data.onRemove && (
          <button
            onClick={() => data.onRemove(data.id)}
            className="w-5 h-5 bg-slate-900 hover:bg-red-950 text-slate-500 hover:text-red-400 border border-slate-800 rounded-md text-[10px] flex items-center justify-center font-bold transition-all opacity-0 group-hover:opacity-100"
          >✕</button>
        )}
      </div>

      {/* Angle Name */}
      <div className="text-white font-black text-sm uppercase tracking-wide truncate mb-1">
        {data.angleName || data.label || 'Landing Page'}
      </div>

      {/* Funnel Type chip */}
      {data.funnelType && (
        <div
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold border mb-3"
          style={{ background: `${ftColor}18`, borderColor: `${ftColor}40`, color: ftColor }}
        >
          {data.funnelType}
        </div>
      )}

      {/* Page built warning / Stats grid */}
      {!isPageBuilt ? (
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[9.5px] font-bold py-2 px-3 rounded-xl mb-4 leading-relaxed font-sans text-center">
          ⚠️ Page not built yet — click Build Page to start
        </div>
      ) : (
        <div className="bg-slate-900/60 border border-slate-900/40 rounded-xl p-3 mb-4 text-left">
          <div className="flex justify-between items-center text-[7.5px] font-black text-slate-500 uppercase tracking-widest mb-1.5 border-b border-slate-850 pb-1">
            <span>📈 7-Day Performance</span>
            {realStats && <span className="text-indigo-400 font-bold">7D LIVE</span>}
          </div>
          <div className="grid grid-cols-3 gap-1 text-center">
            <div>
              <div className="text-[6.5px] text-slate-500 uppercase font-black">Views (7d)</div>
              <div className="text-[9.5px] text-slate-200 font-mono font-black">{displayViews}</div>
            </div>
            <div className="border-x border-slate-850">
              <div className="text-[6.5px] text-slate-500 uppercase font-black">Clicks</div>
              <div className="text-[9.5px] text-slate-200 font-mono font-black">{displayClicks}</div>
            </div>
            <div>
              <div className="text-[6.5px] text-slate-500 uppercase font-black">CTR</div>
              <div className={`text-[9.5px] font-mono font-black ${realStats && realStats.views === 0 ? 'text-slate-400' : 'text-emerald-400'}`}>{displayCtr}</div>
            </div>
          </div>
          <div className="flex justify-between items-center px-1 pt-1.5 mt-1 border-t border-slate-850/60 text-[7px] text-slate-400">
            <span>unique visitors (per session):</span>
            <span className="text-slate-200 font-mono font-bold">{displayUnique}</span>
          </div>
        </div>
      )}

      {/* Autoresponder Status */}
      {(() => {
        const hasAr = !!(data.mailerXpList || data.pageContent?.mailerXpList);
        const customForm = data.customForm || data.pageContent?.customForm;
        const isCustom = !!(customForm?.action);
        if (hasAr || isCustom) {
          return (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[8.5px] font-bold mb-2">
              <span>📬</span>
              <span>{isCustom ? (customForm.provider || 'Custom Form') : 'MailerXP Active'}</span>
            </div>
          );
        }
        return null;
      })()}

      {/* Action Buttons */}
      <div className="space-y-2">
        {!isPageBuilt ? (
          <button
            type="button"
            onClick={() => data.onBuildPage && data.onBuildPage(data)}
            className="w-full py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-1.5 cursor-pointer"
            style={{
              background: `linear-gradient(135deg, #f97316, #ea580c)`,
              color: '#fff',
              boxShadow: '0 4px 14px rgba(249,115,22,0.3)',
            }}
          >
            🛠 Build Page
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => data.onEdit && data.onEdit()}
              className="flex-1 py-2 rounded-xl text-[9.5px] font-black uppercase tracking-widest border border-emerald-500/30 hover:border-emerald-400 text-emerald-400 bg-emerald-500/10 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1"
            >
              ✏️ Edit Page
            </button>
            <button
              type="button"
              onClick={() => data.onBuildPage && data.onBuildPage(data)}
              className="py-2 px-3 rounded-xl text-[9.5px] font-black border border-white/10 hover:border-white/20 text-slate-400 hover:text-white transition-all active:scale-95 cursor-pointer"
              title="Rebuild Page"
            >
              🔄 Rebuild
            </button>
          </div>
        )}

        {/* Set Active button for queued angles */}
        {!isActive && data.onActivate && (
          <button
            type="button"
            onClick={() => data.onActivate(data)}
            className="w-full py-1.5 rounded-xl text-[9px] font-bold uppercase tracking-wider border transition-all cursor-pointer"
            style={{ borderColor: `${cfg.color}40`, color: cfg.color, background: cfg.bg }}
          >
            Set Active
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Node / edge maps ────────────────────────────────────────────────────────
const nodeTypes = {
  traffic: TrafficNode,
  page:    PageNode,
  custom:  PageNode, // Match App.jsx page stage creation
  capture: CaptureNode,
  offer:   OfferNode,
  landing: AngleNode, // Map landing to AngleNode so it has Build Page
  angle:   AngleNode,  // Angle Planning System
};
const edgeTypes = { animated: AnimatedEdge };

// ─── Main Canvas Visualizer ──────────────────────────────────────────────────
const FunnelVisualizer = ({
  trafficSource = 'General Traffic',
  pages = [],
  network = 'Merchant Offer',
  campaignId,
  campaign,        // full campaign object (for PageBuilderAgent)
  slug,
  username,
  customDomain,
  onEditPage,
  onDuplicatePage,
  onAddPage,
  onRenamePage,
  savedArchitecture,
  onSaveArchitecture,
  onChangeTrafficSource,
  onOpenAdStudio,
  initialLink,
  selectedStrategies = [],
  onAngleStatusChange,  // callback(campaignId, angleId, newStatus)
  onPageBuilt,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showTrafficSelector, setShowTrafficSelector] = useState(false);

  // ── 7-Day Real Performance Stats (Views, Clicks, CTR, Unique Visitors) ────
  const [livePageStats, setLivePageStats] = useState({});

  useEffect(() => {
    const targetCampaignId = campaignId || campaign?.id;
    if (!targetCampaignId) return;
    let isMounted = true;

    const fetchStats = async () => {
      // Pause polling when tab is hidden
      if (document.hidden) return;
      try {
        const headers = {};
        if (auth?.currentUser) {
          try {
            const idToken = await auth.currentUser.getIdToken();
            if (idToken) {
              headers['Authorization'] = `Bearer ${idToken}`;
            }
          } catch (tokErr) {
            console.warn('[PAGE-STATS TOKEN ERROR]', tokErr);
          }
        }
        const res = await fetch(`/api/campaigns/${targetCampaignId}/page-stats`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (isMounted) setLivePageStats(data);
        }
      } catch (err) {
        console.warn('[PAGE-STATS FETCH ERROR]', err);
      }
    };

    fetchStats();

    // 60-second polling interval
    const timer = setInterval(fetchStats, 60000);

    // Refresh immediately when tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchStats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also trigger fetch when user authentication state changes/resolves
    const unsubAuth = auth ? onAuthStateChanged(auth, (user) => {
      if (user && isMounted) {
        fetchStats();
      }
    }) : () => {};

    return () => {
      isMounted = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unsubAuth();
    };
  }, [campaignId, campaign?.id]);

  // Inject real stats into nodes whenever stats update
  useEffect(() => {
    if (!livePageStats || Object.keys(livePageStats).length === 0) return;
    setNodes(prev => prev.map(n => {
      const stats = livePageStats[n.id] 
        || (n.data?.angleId ? livePageStats[n.data.angleId] : null)
        || livePageStats[`${campaignId || campaign?.id}_${n.id}`]
        || (n.data?.label ? livePageStats[n.data.label] : null);
      if (stats) {
        return {
          ...n,
          data: {
            ...n.data,
            pageStats: stats
          }
        };
      }
      return n;
    }));
  }, [livePageStats, campaignId, campaign?.id]);

  // ── Angle Planning System state ───────────────────────────────────────────
  const [pageBuilderAngle, setPageBuilderAngle] = useState(null); // angle data for modal
  const [showPageBuilder, setShowPageBuilder] = useState(false);
  const [angleActivateConfirm, setAngleActivateConfirm] = useState(null); // angle data awaiting confirm
  const [coachBannerDismissed, setCoachBannerDismissed] = useState(false);

  // Derive 500-click coach alert from active angle plan
  const activeAngle500Click = useMemo(() => {
    if (!campaign?.anglePlans) return null;
    const active = campaign.anglePlans.find(a => a.status === 'active');
    if (active && (active.clicks || 0) >= 500) return active;
    return null;
  }, [campaign]);

  // Handler: open PageBuilderAgentModal for an angle node
  const handleBuildPage = useCallback((angleData) => {
    setPageBuilderAngle(angleData);
    setShowPageBuilder(true);
  }, []);

  // Handler: request angle activation (shows confirm dialog or warning if page not built)
  const handleActivateAngle = useCallback((angleData) => {
    if (!angleData.isPageBuilt) {
      setAngleActivateConfirm({
        ...angleData,
        error: 'You must build the page for this angle before activating it.'
      });
    } else {
      setAngleActivateConfirm(angleData);
    }
  }, []);

  // Handler: confirm angle activation
  const confirmActivateAngle = useCallback(() => {
    if (!angleActivateConfirm || angleActivateConfirm.error) return;
    const angleId = angleActivateConfirm.angleId;
    let nextNodes = [];
    // Update nodes locally
    setNodes(curr => {
      nextNodes = curr.map(n => {
        if (n.type !== 'angle' && n.type !== 'traffic') return n;
        if (n.data.angleId) {
          const newStatus = n.data.angleId === angleId ? 'active' : 'queued';
          return {
            ...n,
            data: { ...n.data, angleStatus: newStatus },
          };
        }
        return n;
      });
      return nextNodes;
    });
    // Notify parent
    if (onAngleStatusChange && campaignId) {
      onAngleStatusChange(campaignId, angleId, 'active');
    }
    // Persist architecture status changes to Firestore
    if (onSaveArchitecture) {
      setTimeout(() => {
        if (nextNodes && nextNodes.length > 0) {
          onSaveArchitecture(nextNodes, edges);
        }
      }, 50);
    }
    setAngleActivateConfirm(null);
  }, [angleActivateConfirm, campaignId, onAngleStatusChange, onSaveArchitecture, edges]);

  // Sync / Load Campaign Architecture
  useEffect(() => {
    if (savedArchitecture && savedArchitecture.nodes && savedArchitecture.nodes.length > 0) {
      // Load saved nodes & enrich with interactive React context callbacks
      setNodes(prevNodes => {
        const enriched = savedArchitecture.nodes.map(savedNode => {
          let enrichedData = { ...savedNode.data };
          if (savedNode.type === 'traffic') {
            enrichedData.onOpenAdStudio = onOpenAdStudio;
            enrichedData.initialLink = initialLink;
            enrichedData.onRemove = handleRemoveNode;
            enrichedData.id = savedNode.id;
          } else if (savedNode.type === 'page' || savedNode.type === 'custom' || savedNode.type === 'video-hub' || savedNode.type === 'listicle') {
            if (savedNode.id?.startsWith('angle') || savedNode.data?.angleId) {
              enrichedData.onBuildPage = handleBuildPage;
              enrichedData.onActivate = handleActivateAngle;
              enrichedData.onRemove = handleRemoveNode;
              enrichedData.id = savedNode.id;
              enrichedData.isPageBuilt = pages.some(p => p === savedNode.id || p === savedNode.data?.angleId || p === `page_${savedNode.data?.angleId}` || p === `page_${savedNode.id}`);
              enrichedData.onEdit = onEditPage ? () => onEditPage(savedNode.id) : undefined;
            } else {
              enrichedData.onEdit = onEditPage ? () => onEditPage(savedNode.id) : undefined;
              enrichedData.onDuplicate = onDuplicatePage ? () => onDuplicatePage(savedNode.id) : undefined;
              enrichedData.onChangeStage = (newStage) => handleChangePageStage(savedNode.id, newStage);
              enrichedData.onChangeOfferCount = (newCount) => handleChangePageOfferCount(savedNode.id, newCount);
              enrichedData.onRemove = handleRemoveNode;
              enrichedData.id = savedNode.id;
            }
          } else if (savedNode.type === 'capture' || savedNode.type === 'capture-popup') {
            enrichedData.onUpdateConfig = handleUpdateCaptureConfig;
            enrichedData.onEdit = onEditPage ? () => onEditPage(savedNode.id) : undefined;
            enrichedData.onRemove = handleRemoveNode;
            enrichedData.id = savedNode.id;
          } else if (savedNode.type === 'offer') {
            enrichedData.onUpdateLink = handleUpdateOfferLink;
            enrichedData.onRemove = handleRemoveNode;
            enrichedData.id = savedNode.id;
          } else if (savedNode.type === 'angle' || savedNode.type === 'landing' || savedNode.id?.startsWith('angle') || savedNode.data?.angleId) {
            // Angle Planning System node
            enrichedData.onBuildPage = handleBuildPage;
            enrichedData.onActivate = handleActivateAngle;
            enrichedData.onRemove = handleRemoveNode;
            enrichedData.id = savedNode.id;
            enrichedData.isPageBuilt = pages.some(p => p === savedNode.id || p === savedNode.data.angleId || p === `page_${savedNode.data.angleId}` || p === `page_${savedNode.id}`);
            enrichedData.onEdit = onEditPage ? () => onEditPage(savedNode.id) : undefined;
            enrichedData.mailerXpList = savedNode.pageContent?.mailerXpList || savedNode.data?.mailerXpList || campaign?.mailerXpList;
            enrichedData.customForm = savedNode.pageContent?.customForm || savedNode.data?.customForm || campaign?.customForm;
          }

          enrichedData.pageStats = livePageStats[savedNode.id] 
            || (savedNode.data?.angleId ? livePageStats[savedNode.data.angleId] : null)
            || livePageStats[`${campaignId || campaign?.id}_${savedNode.id}`]
            || (savedNode.data?.label ? livePageStats[savedNode.data.label] : null)
            || null;

          // Keep user's drag positions to avoid jittery resets during autosave
          const localNode = prevNodes.find(n => n.id === savedNode.id);
          return {
            ...savedNode,
            position: localNode ? localNode.position : savedNode.position,
            data: enrichedData
          };
        });

        // Autocomplete with a default traffic node if one is missing in saved nodes
        const hasTraffic = enriched.some(n => n.type === 'traffic');
        if (!hasTraffic) {
          enriched.push({
            id: 'traffic',
            type: 'traffic',
            position: { x: -100, y: 150 },
            title: 'Custom Tracker',
            label: 'Custom Tracker',
            pageContent: {
              sourceProvider: 'custom',
              customSource: 'googleads'
            },
            data: {
              label: 'Custom Tracker',
              onOpenAdStudio,
              initialLink,
              onRemove: handleRemoveNode,
              id: 'traffic'
            }
          });
        }
        return enriched;
      });

      setEdges(prevEdges => {
        let loadedEdges = [];
        if (savedArchitecture.edges) {
          loadedEdges = savedArchitecture.edges.map(edge => ({
            ...edge,
            type: 'animated'
          }));
        } else if (savedArchitecture.connections) {
          loadedEdges = savedArchitecture.connections.map(conn => ({
            id: conn.id || `edge-${conn.from}-to-${conn.to}`,
            source: conn.from,
            target: conn.to,
            sourceHandle: conn.fromPort,
            targetHandle: conn.toPort,
            type: 'animated'
          }));
        }

        // Connect the dynamic default traffic node if no traffic edge exists
        const hasTrafficEdge = loadedEdges.some(e => e.source === 'traffic' || e.source.startsWith('traffic_'));
        if (!hasTrafficEdge) {
          // Find first page node to connect to
          const landingNode = savedArchitecture.nodes.find(n => 
            n.id === 'landing' || 
            n.id === 'page_landing' || 
            n.type === 'landing' || 
            n.type === 'page' || 
            n.type === 'custom' || 
            n.id.startsWith('page_')
          );
          if (landingNode) {
            loadedEdges.push({
              id: `edge-traffic-to-${landingNode.id}`,
              source: 'traffic',
              target: landingNode.id,
              type: 'animated'
            });
          }
        }
        return loadedEdges;
      });
    } else {
      // Establish default flow structure for new / empty campaigns
      const initialNodes = [];
      const initialEdges = [];
      const pageList = pages.length > 0 ? pages : ['landing'];

      if (selectedStrategies && selectedStrategies.length > 1) {
        // Multi-strategy parallel routing!
        const hasCapture = pageList.includes('capture');
        
        selectedStrategies.forEach((strat, idx) => {
          const yPos = 80 + idx * 160;
          // Normalize traffic node id
          const trafficNodeId = `traffic_${strat.trafficSource.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
          const pageName = strat.funnelType || 'landing';
          const pageNodeId = `page_${pageName}`;

          // 1. Traffic Node
          initialNodes.push({
            id: trafficNodeId,
            type: 'traffic',
            position: { x: 50, y: yPos },
            data: {
              label: strat.trafficSource,
              onOpenAdStudio,
              initialLink,
              onRemove: handleRemoveNode,
              id: trafficNodeId
            }
          });

          // 2. Pre-sell Page Node
          initialNodes.push({
            id: pageNodeId,
            type: 'page',
            position: { x: 320, y: yPos },
            data: {
              label: pageName,
              onEdit: onEditPage ? () => onEditPage(pageName) : undefined,
              onDuplicate: onDuplicatePage ? () => onDuplicatePage(pageName) : undefined,
              onRemove: handleRemoveNode,
              id: pageNodeId
            }
          });

          // 3. Traffic to Page Edge
          initialEdges.push({
            id: `edge-${trafficNodeId}-to-${pageNodeId}`,
            source: trafficNodeId,
            target: pageNodeId,
            type: 'animated'
          });

          // 4. Page to Next Edge
          if (hasCapture) {
            initialEdges.push({
              id: `edge-${pageNodeId}-to-page_capture`,
              source: pageNodeId,
              target: 'page_capture',
              type: 'animated'
            });
          } else {
            initialEdges.push({
              id: `edge-${pageNodeId}-to-offer`,
              source: pageNodeId,
              target: 'offer',
              type: 'animated'
            });
          }
        });

        // Add shared capture node if present
        if (hasCapture) {
          initialNodes.push({
            id: 'page_capture',
            type: 'capture',
            position: { x: 590, y: 160 },
            data: {
              label: 'capture',
              onUpdateConfig: handleUpdateCaptureConfig,
              onEdit: onEditPage ? () => onEditPage('capture') : undefined,
              onRemove: handleRemoveNode,
              id: 'page_capture'
            }
          });

          // Edge from Capture to Offer
          initialEdges.push({
            id: 'edge-capture-to-offer',
            source: 'page_capture',
            target: 'offer',
            type: 'animated'
          });
        }

        // Add Merchant Offer Node
        initialNodes.push({
          id: 'offer',
          type: 'offer',
          position: { x: hasCapture ? 860 : 590, y: 160 },
          data: {
            label: network || 'Offer',
            onUpdateLink: handleUpdateOfferLink,
            onRemove: handleRemoveNode,
            id: 'offer'
          }
        });

        setNodes(initialNodes);
        setEdges(initialEdges);
      } else {
        // Fallback to general sequential flow
        // Start Traffic Node
        initialNodes.push({
          id: 'traffic',
          type: 'traffic',
          position: { x: 50, y: 150 },
          data: {
            label: trafficSource,
            onOpenAdStudio,
            initialLink,
            onRemove: handleRemoveNode,
            id: 'traffic'
          }
        });

        // Horizontal sequential pages
        pageList.forEach((pageName, idx) => {
          initialNodes.push({
            id: `page_${pageName}`,
            type: 'page',
            position: { x: 320 + idx * 280, y: 150 },
            data: {
              label: pageName,
              onEdit: onEditPage ? () => onEditPage(pageName) : undefined,
              onDuplicate: onDuplicatePage ? () => onDuplicatePage(pageName) : undefined,
              onRemove: handleRemoveNode,
              id: `page_${pageName}`
            }
          });
        });

        // End Offer Node
        initialNodes.push({
          id: 'offer',
          type: 'offer',
          position: { x: 320 + pageList.length * 280, y: 150 },
          data: {
            label: network || 'Offer',
            onUpdateLink: handleUpdateOfferLink,
            onRemove: handleRemoveNode,
            id: 'offer'
          }
        });

        // Sequential connections
        initialEdges.push({
          id: 'edge-traffic-to-landing',
          source: 'traffic',
          target: `page_${pageList[0]}`,
          type: 'animated'
        });

        for (let i = 0; i < pageList.length - 1; i++) {
          initialEdges.push({
            id: `edge-page-${pageList[i]}-to-${pageList[i+1]}`,
            source: `page_${pageList[i]}`,
            target: `page_${pageList[i+1]}`,
            type: 'animated'
          });
        }

        initialEdges.push({
          id: 'edge-page-to-offer',
          source: `page_${pageList[pageList.length - 1]}`,
          target: 'offer',
          type: 'animated'
        });

        setNodes(initialNodes);
        setEdges(initialEdges);

        // Sync this newly established default structure back to the parent component
        if (onSaveArchitecture) {
          onSaveArchitecture(initialNodes, initialEdges);
        }
      }
    }
  }, [campaignId, savedArchitecture, onSaveArchitecture]);

  // ─── Actions & Callbacks ──────────────────────────────────────────────────
  
  const handleUpdateCaptureConfig = useCallback((id, config) => {
    setNodes(currentNodes => {
      const next = currentNodes.map(n => {
        if (n.id === id) {
          return { ...n, data: { ...n.data, config } };
        }
        return n;
      });
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
  }, [edges, onSaveArchitecture]);

  const handleUpdateOfferLink = useCallback((id, affiliateLink) => {
    setNodes(currentNodes => {
      const next = currentNodes.map(n => {
        if (n.id === id) {
          return { ...n, data: { ...n.data, affiliateLink } };
        }
        return n;
      });
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
  }, [edges, onSaveArchitecture]);

  const handleChangePageStage = useCallback((nodeId, newStage) => {
    setNodes(currentNodes => {
      const next = currentNodes.map(n => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: {
              ...n.data,
              stage: newStage
            }
          };
        }
        return n;
      });
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
  }, [edges, onSaveArchitecture]);

  const handleChangePageOfferCount = useCallback((nodeId, newCount) => {
    setNodes(currentNodes => {
      const next = currentNodes.map(n => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: {
              ...n.data,
              offerCount: newCount
            }
          };
        }
        return n;
      });
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
  }, [edges, onSaveArchitecture]);

  const handleTrafficSourceChange = useCallback((nodeId, newLabel) => {
    setNodes(currentNodes => {
      const next = currentNodes.map(n => {
        if (n.id === nodeId) {
          return { ...n, data: { ...n.data, label: newLabel } };
        }
        return n;
      });
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
    if (nodeId === 'traffic' && onChangeTrafficSource) {
      onChangeTrafficSource(newLabel);
    }
  }, [edges, onSaveArchitecture, onChangeTrafficSource]);
  const [insertEdgeInfo, setInsertEdgeInfo] = useState(null);

  const handleRemoveNode = useCallback((id) => {
    setNodes(currentNodes => {
      const nextNodes = currentNodes.filter(n => n.id !== id);
      setEdges(currentEdges => {
        const nextEdges = currentEdges.filter(e => e.source !== id && e.target !== id);
        if (onSaveArchitecture) onSaveArchitecture(nextNodes, nextEdges);
        return nextEdges;
      });
      return nextNodes;
    });
  }, [onSaveArchitecture]);

  const handleRemoveEdge = useCallback((edgeId) => {
    setEdges(currentEdges => {
      const nextEdges = currentEdges.filter(e => e.id !== edgeId);
      if (onSaveArchitecture) onSaveArchitecture(nodes, nextEdges);
      return nextEdges;
    });
  }, [nodes, onSaveArchitecture]);

  const handleInsertNode = useCallback((edgeId) => {
    const edge = edges.find(e => e.id === edgeId);
    if (edge) {
      setInsertEdgeInfo(edge);
      setShowAddDropdown(true);
    }
  }, [edges]);

  // React Flow Handlers
  const onConnect = useCallback((connection) => {
    setEdges((eds) => {
      const nextEdges = addEdge({ ...connection, type: 'animated' }, eds);
      if (onSaveArchitecture) onSaveArchitecture(nodes, nextEdges);
      return nextEdges;
    });
  }, [nodes, onSaveArchitecture]);

  const onNodesDelete = useCallback((deleted) => {
    setNodes(currentNodes => {
      const nextNodes = currentNodes.filter(n => !deleted.some(d => d.id === n.id));
      setEdges(currentEdges => {
        const nextEdges = currentEdges.filter(
          e => !deleted.some(d => d.id === e.source || d.id === e.target)
        );
        if (onSaveArchitecture) onSaveArchitecture(nextNodes, nextEdges);
        return nextEdges;
      });
      return nextNodes;
    });
  }, [onSaveArchitecture]);

  const onEdgesDelete = useCallback((deleted) => {
    setEdges(currentEdges => {
      const nextEdges = currentEdges.filter(e => !deleted.some(d => d.id === e.id));
      if (onSaveArchitecture) onSaveArchitecture(nodes, nextEdges);
      return nextEdges;
    });
  }, [nodes, onSaveArchitecture]);

  const onNodeDragStop = useCallback(() => {
    if (onSaveArchitecture) {
      onSaveArchitecture(nodes, edges);
    }
  }, [nodes, edges, onSaveArchitecture]);

  // ─── Floating Node Creators ───────────────────────────────────────────────

  const addFloatingTraffic = (selectedSource) => {
    const newId = `traffic_${Date.now()}`;
    const newNode = {
      id: newId,
      type: 'traffic',
      position: { x: 100 + Math.random() * 80, y: 150 + Math.random() * 80 },
      data: {
        label: selectedSource,
        onOpenAdStudio,
        initialLink,
        onRemove: handleRemoveNode,
        id: newId
      }
    };
    setNodes(current => {
      const next = [...current, newNode];
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });

    if (onChangeTrafficSource) {
      onChangeTrafficSource(selectedSource);
    }

    setInsertEdgeInfo(null);
    setShowAddDropdown(false);
  };

  const addFloatingPage = (type) => {
    const position = { x: 300 + Math.random() * 120, y: 150 + Math.random() * 120 };
    onAddPage(type, position, insertEdgeInfo);
    setInsertEdgeInfo(null);
    setShowAddDropdown(false);
  };

  const addFloatingCapture = () => {
    const newId = `capture-${Date.now()}`;
    const newNode = {
      id: newId,
      type: 'capture',
      position: { x: 300 + Math.random() * 120, y: 150 + Math.random() * 120 },
      data: {
        id: newId,
        config: {
          headline: 'Get Instant Access',
          subtext: 'Enter your email to continue',
          btnText: 'Yes, Give Me Access →',
          listId: '',
        },
        onUpdateConfig: handleUpdateCaptureConfig,
        onRemove: handleRemoveNode
      }
    };
    setNodes(current => {
      const next = [...current, newNode];
      
      if (insertEdgeInfo) {
        setEdges(currentEdges => {
           const nextEdges = currentEdges.filter(e => e.id !== insertEdgeInfo.id);
           nextEdges.push({
             id: `edge-${insertEdgeInfo.source}-to-${newNode.id}`,
             source: insertEdgeInfo.source,
             target: newNode.id,
             type: 'animated'
           });
           nextEdges.push({
             id: `edge-${newNode.id}-to-${insertEdgeInfo.target}`,
             source: newNode.id,
             target: insertEdgeInfo.target,
             type: 'animated'
           });
           if (onSaveArchitecture) onSaveArchitecture(next, nextEdges);
           return nextEdges;
        });
      } else {
        if (onSaveArchitecture) onSaveArchitecture(next, edges);
      }
      return next;
    });
    setInsertEdgeInfo(null);
    setShowAddDropdown(false);
  };

  const addFloatingOffer = () => {
    const newId = `offer-${Date.now()}`;
    const newNode = {
      id: newId,
      type: 'offer',
      position: { x: 600 + Math.random() * 120, y: 150 + Math.random() * 120 },
      data: {
        label: 'Affiliate Offer',
        affiliateLink: '',
        onUpdateLink: handleUpdateOfferLink,
        onRemove: handleRemoveNode,
        id: newId
      }
    };
    setNodes(current => {
      const next = [...current, newNode];
      if (onSaveArchitecture) onSaveArchitecture(next, edges);
      return next;
    });
    setShowAddDropdown(false);
  };
  const edgesWithData = useMemo(() => {
    return edges.map(e => ({
      ...e,
      data: {
        ...e.data,
        onDelete: handleRemoveEdge,
        onInsert: handleInsertNode
      }
    }));
  }, [edges, handleRemoveEdge, handleInsertNode]);

  return (
    <div className="w-full h-full relative" style={{ minHeight: '520px' }}>

      {/* ── 500-Click Coach Banner ── */}
      {activeAngle500Click && !coachBannerDismissed && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 w-full max-w-lg px-4">
          <div className="bg-[#f97316]/10 border border-[#f97316]/40 backdrop-blur rounded-2xl px-5 py-3.5 flex items-start gap-3 shadow-2xl">
            <span className="text-lg shrink-0">🤖</span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-[#f97316] uppercase tracking-wider">Coach Tip — 500 Clicks Reached</div>
              <p className="text-[11px] text-slate-300 mt-0.5 font-sans leading-relaxed">
                Your active angle <strong className="text-white">&ldquo;{activeAngle500Click.name}&rdquo;</strong> has reached 500 clicks.
                Review your conversion data and consider activating the next angle to compare performance.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCoachBannerDismissed(true)}
              className="shrink-0 text-slate-500 hover:text-white transition-colors mt-0.5"
            >✕</button>
          </div>
        </div>
      )}

      {/* ── Angle Activate Confirmation Dialog ── */}
      {angleActivateConfirm && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#13161d] border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl space-y-4 font-mono">
            {angleActivateConfirm.error ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <div className="text-sm font-bold text-white">Build Page First</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Landing page required</div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                  You must build the landing page for <strong className="text-[#f97316]">{angleActivateConfirm.angleName || angleActivateConfirm.label}</strong> before you can activate it.
                </p>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setAngleActivateConfirm(null)}
                    className="w-full py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer text-center"
                  >
                    Got It
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚡</span>
                  <div>
                    <div className="text-sm font-bold text-white">Activate This Angle?</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Setting <strong className="text-[#f97316]">{angleActivateConfirm.angleName || angleActivateConfirm.label}</strong> as Active</div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                  All other angles will remain <strong className="text-amber-400">Queued</strong>. Only one angle can be Active at a time. The page is already built and ready to receive traffic.
                </p>
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setAngleActivateConfirm(null)}
                    className="flex-1 py-2.5 rounded-xl border border-white/10 text-slate-400 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={confirmActivateAngle}
                    className="flex-1 py-2.5 rounded-xl bg-[#22c55e] hover:bg-[#16a34a] text-white text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer shadow-lg shadow-[#22c55e]/20"
                  >Activate</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Page Builder Agent Modal ── */}
      <PageBuilderAgentModal
        isOpen={showPageBuilder}
        onClose={() => setShowPageBuilder(false)}
        campaign={campaign}
        angle={pageBuilderAngle}
        coachResult={campaign?.coachAnalysis || null}
        onPageBuilt={(html, coach) => {
          setShowPageBuilder(false);
          const angleId = pageBuilderAngle?.angleId;
          if (angleId) {
            const pageId = `page_${angleId}`;
            if (onPageBuilt) {
              onPageBuilt(pageId, html);
            }
            // Set isPageBuilt to true locally on the node in the map
            setNodes(curr => {
              const updated = curr.map(n => {
                if (n.type === 'angle' && n.data.angleId === angleId) {
                  return {
                    ...n,
                    data: { ...n.data, isPageBuilt: true }
                  };
                }
                return n;
              });
              if (onSaveArchitecture) {
                onSaveArchitecture(updated, edges);
              }
              return updated;
            });
          }
        }}
      />
      <style>{`
        @keyframes dash { to { stroke-dashoffset: -20; } }
        .react-flow__attribution { display: none; }
      `}</style>

      {/* Legend / Status Hint Indicator */}
      <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
        <div className="bg-slate-950/90 backdrop-blur border border-slate-900 rounded-2xl px-4 py-2.5 flex items-center gap-4 shadow-2xl text-[9px] font-black uppercase tracking-widest text-slate-400">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow shadow-amber-400/50 inline-block"></span>Traffic</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-indigo-400 shadow shadow-indigo-400/50 inline-block"></span>Page</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-purple-400 shadow shadow-purple-400/50 inline-block"></span>Opt-In Popup</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow shadow-emerald-400/50 inline-block"></span>Offer</span>
        </div>
        <div className="text-[9px] font-bold text-slate-500 italic px-1">
          💡 Drag & drop nodes to organize. Drag handles to connect. Click node/edge and press Del to delete.
        </div>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edgesWithData}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        className="bg-slate-950"
        minZoom={0.2}
      >
        <Background color="#0f172a" gap={24} size={1} />
        <Controls className="!bg-slate-900 !border-slate-800 !rounded-2xl !shadow-2xl" />
      </ReactFlow>

      {/* Floater Add Node Trigger */}
      <div className="absolute bottom-5 right-5 z-20 flex flex-col items-end">
        {showAddDropdown && (
          <div className="mb-3.5 p-2 bg-slate-900/95 backdrop-blur-xl border border-slate-850 rounded-[1.5rem] shadow-2xl flex flex-col gap-1 w-64 transform origin-bottom transition-all duration-200 animate-in fade-in slide-in-from-bottom-2">
            <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest px-3.5 py-2 border-b border-slate-850 mb-1 flex justify-between items-center">
              <span>{insertEdgeInfo ? 'Insert Node in Path' : 'Add Node to Canvas'}</span>
              {insertEdgeInfo && (
                <button onClick={() => { setInsertEdgeInfo(null); setShowAddDropdown(false); }} className="text-rose-500 hover:text-rose-400 font-bold">✕ Close</button>
              )}
            </div>
            
            {!insertEdgeInfo && (
              <>
                <button
                  onClick={() => {
                    setShowTrafficSelector(true);
                    setShowAddDropdown(false);
                  }}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-amber-600/10 hover:text-amber-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
                >
                  <span className="text-sm">🚦</span> Traffic
                </button>
                <div className="w-full h-px bg-slate-850 my-1"></div>
              </>
            )}

            <button
              onClick={() => addFloatingPage('squeeze')}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-indigo-600/10 hover:text-indigo-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
            >
              <span className="text-sm">📄</span> Landing (Squeeze)
            </button>

            <button
              onClick={() => addFloatingPage('advertorial')}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-amber-600/10 hover:text-amber-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
            >
              <span className="text-sm">📰</span> Advertorial
            </button>

            <button
              onClick={() => addFloatingPage('listicle')}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-cyan-600/10 hover:text-cyan-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
            >
              <span className="text-sm">📑</span> Listicle Page
            </button>

            <button
              onClick={() => addFloatingPage('oto')}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-rose-600/10 hover:text-rose-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
            >
              <span className="text-sm">🔥</span> OTO Page
            </button>

            <button
              onClick={addFloatingCapture}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-purple-600/10 hover:text-purple-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
            >
              <span className="text-sm">📧</span> Capture Popup
            </button>

            {!insertEdgeInfo && (
              <>
                <div className="w-full h-px bg-slate-850 my-1"></div>
                <button
                  onClick={addFloatingOffer}
                  className="w-full flex items-center gap-3 px-3.5 py-2.5 hover:bg-emerald-600/10 hover:text-emerald-400 text-slate-300 text-xs font-bold rounded-xl transition-all text-left"
                >
                  <span className="text-sm">🛒</span> Offer
                </button>
              </>
            )}
          </div>
        )}
        
        {!insertEdgeInfo && (
          <button
            onClick={() => setShowAddDropdown(!showAddDropdown)}
            className="px-5 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white text-xs font-black uppercase tracking-widest rounded-2xl shadow-xl shadow-indigo-500/20 flex items-center gap-2.5 transition-all border border-indigo-500/20 active:scale-95 transform hover:scale-[1.02]"
          >
            {showAddDropdown ? '✕ Close Menu' : '+ Nodes'}
          </button>
        )}
      </div>

      {showTrafficSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 sm:p-10 shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 rounded-t-[2.5rem]"></div>
            
            <button 
              onClick={() => setShowTrafficSelector(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-slate-500 hover:text-white bg-slate-950/40 border border-slate-800 hover:bg-slate-850 hover:border-slate-700 rounded-full transition-all cursor-pointer font-bold"
            >
              ✕
            </button>

            <div className="mb-6 text-left">
              <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest bg-amber-500/10 px-3 py-1.5 rounded-full">
                Campaign Funnel Visualization Routing
              </span>
              <h3 className="text-2xl font-black text-white tracking-tight mt-3">🚦 Add Traffic Node</h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Choose a targeted traffic source. To preserve isolated Ad Studio creatives and campaign tracking consistency, traffic sources are locked once created.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 overflow-y-auto max-h-[350px] pr-2 custom-scrollbar">
              {Object.keys(TRAFFIC_ICONS).map((opt) => {
                const icon = TRAFFIC_ICONS[opt] || '🚦';
                return (
                  <button
                    key={opt}
                    onClick={() => addFloatingTraffic(opt)}
                    className="p-4 bg-slate-950/40 border border-slate-800 hover:border-amber-500/60 rounded-2xl text-left transition-all hover:bg-amber-500/5 group cursor-pointer flex flex-col justify-between h-24"
                  >
                    <span className="text-2xl group-hover:scale-110 transition-transform duration-200">{icon}</span>
                    <span className="font-black text-[11px] text-slate-300 group-hover:text-amber-400 transition-colors uppercase tracking-wider">
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelVisualizer;
